/**
 * ACC-021 to ACC-024: pause, skip, move, cancel, restart, and Stripe's customer portal.
 */
import * as stylex from "@stylexjs/stylex";
import { useEffect, useState } from "react";
import type { AccountSubscription } from "@lattice/core";
import { currentShipOn, MOVE_SHIP_CHOICES, PAUSE_MONTHS, skipShipOn } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { Button } from "@lattice/ui/Button";
import { ConfirmDialog } from "@lattice/ui/ConfirmDialog";
import { pageHeading } from "@lattice/ui/pageHeading";
import { ErrorState } from "@lattice/ui/ErrorState";
import { LinkButton } from "@lattice/ui/LinkButton";
import { Pending } from "@lattice/ui/Pending";
import { Status } from "@lattice/ui/Status";
import { captureEmail, changeAccountSubscription, loadAccountSubscription, openAccountPortal, signedInEmail } from "../api.ts";
import { turnstileToken } from "../turnstile.ts";
import { AccountShell, TRY_AGAIN_THEN_SUPPORT } from "./Shell.tsx";
const DAY = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  year: "numeric",
  timeZone: "UTC"
});
type Screen = {
  readonly kind: "loading";
} | {
  readonly kind: "signed_out";
} | {
  readonly kind: "failed";
} | {
  readonly kind: "unconfirmed";
} | {
  readonly kind: "ready";
  readonly sub: AccountSubscription;
};
type Panel = "pause" | "move" | "cancel" | null;
type Action = "pause" | "skip" | "move" | "cancel" | "restart";
function day(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : DAY.format(date);
}
function Lead({
  sub
}: {
  readonly sub: AccountSubscription;
}) {
  if (sub.status === "none") {
    return <p className="x1ghz6dp x1jty25b x1fzhlzt">No subscription yet. It starts after you check out.</p>;
  }
  if (sub.status === "paused" && sub.pausedUntil !== null) {
    return <p className="x1ghz6dp x1jty25b x1fzhlzt">
        Paused until {day(sub.pausedUntil)}. Nothing ships until then.
      </p>;
  }
  if (sub.status === "past_due") {
    return <p className="x1ghz6dp x1jty25b x1fzhlzt">
        The last charge did not go through. Update your card to keep shipping.
      </p>;
  }
  if (sub.status === "cancelled") {
    return <p className="x1ghz6dp x1jty25b x1fzhlzt">
        This subscription has ended. Your formulas, labs and orders stay here.
      </p>;
  }
  if (sub.endingOn !== null) {
    return <p className="x1ghz6dp x1l1vl4p xqu410z">This subscription ends on {day(sub.endingOn)}.</p>;
  }
  if (sub.nextShipOn !== null) {
    return <p className="x1ghz6dp x1l1vl4p xqu410z">Next pouch ships on {day(sub.nextShipOn)}.</p>;
  }
  return <p className="x1ghz6dp x1jty25b x1fzhlzt">You can pause, skip a month, or cancel.</p>;
}
function PausePanel({
  busy,
  onPause,
  onBack
}: {
  readonly busy: boolean;
  readonly onPause: (months: string) => void;
  readonly onBack: () => void;
}) {
  return <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl" data-testid="pause-panel">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">Pause for 1, 2 or 3 months. Nothing ships until then.</p>
      <div className="x78zum5 x1a02dak x626il7">
        {PAUSE_MONTHS.map(months => <Button key={months} tone="quiet" testId={`pause-${String(months)}`} busy={busy} onClick={() => onPause(String(months))}>
            {months === 1 ? "1 month" : `${String(months)} months`}
          </Button>)}
        <Button tone="quiet" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>;
}
function MovePanel({
  busy,
  onMove,
  onBack
}: {
  readonly busy: boolean;
  readonly onMove: (days: string) => void;
  readonly onBack: () => void;
}) {
  return <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl" data-testid="move-panel">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">Move the next ship date by 7, 14 or 30 days.</p>
      <div className="x78zum5 x1a02dak x626il7">
        {MOVE_SHIP_CHOICES.map(days => <Button key={days} tone="quiet" testId={`move-${String(days)}`} busy={busy} onClick={() => onMove(String(days))}>
            {`${String(days)} days`}
          </Button>)}
        <Button tone="quiet" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>;
}
function CancelPanel({
  busy,
  onPause,
  onSkip,
  onConfirm,
  canSkip,
  onBack
}: {
  readonly busy: boolean;
  readonly canSkip: boolean;
  readonly onPause: () => void;
  readonly onSkip: () => void;
  readonly onConfirm: () => void;
  readonly onBack: () => void;
}) {
  return <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl" data-testid="cancel-panel">
      <p className="x1ghz6dp x1jty25b x1fzhlzt">Your physician-written formula stops.</p>
      <p className="x1ghz6dp x1jty25b x1fzhlzt">Future pouches are not mixed or shipped.</p>
      <p className="x1ghz6dp x1jty25b x1fzhlzt">You can pause or skip once instead.</p>
      <div className="x78zum5 x1a02dak x626il7">
        <Button tone="quiet" testId="cancel-pause" busy={busy} onClick={onPause}>
          Pause
        </Button>
        {canSkip ? <Button tone="quiet" testId="cancel-skip" busy={busy} onClick={onSkip}>
            Skip next pouch
          </Button> : null}
        <Button variant="danger" testId="confirm-cancel" loading={busy} onClick={onConfirm}>
          End subscription
        </Button>
        <Button tone="quiet" onClick={onBack}>
          Back
        </Button>
      </div>
    </div>;
}
function RestartPath({
  sub,
  busy,
  onRestart
}: {
  readonly sub: AccountSubscription;
  readonly busy: boolean;
  readonly onRestart: () => void;
}) {
  if (sub.status === "cancelled" && sub.needsReview) {
    return <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl">
        <p className="x1ghz6dp x1jty25b x1fzhlzt">Your labs are older than 6 months.</p>
        <p className="x1ghz6dp x1jty25b x1fzhlzt">
          Upload new labs. A physician will review them before shipping starts again.
        </p>
        <LinkButton href="/start">Upload new labs</LinkButton>
      </div>;
  }
  if (sub.status === "paused" || sub.endingOn !== null) {
    return <Button testId="restart" busy={busy} onClick={onRestart}>
        {sub.status === "paused" ? "Resume" : "Keep subscription"}
      </Button>;
  }
  if (sub.status === "cancelled") {
    return <Button testId="restart" busy={busy} onClick={onRestart}>
        Restart
      </Button>;
  }
  return null;
}
function LiveActions({
  sub,
  busy,
  onPanel,
  onSkip
}: {
  readonly sub: AccountSubscription;
  readonly busy: boolean;
  readonly onPanel: (panel: Panel) => void;
  readonly onSkip: () => void;
}) {
  if (sub.status !== "active" || sub.endingOn !== null) return null;
  return <>
      <Button tone="quiet" testId="pause" busy={busy} onClick={() => onPanel("pause")}>
        Pause
      </Button>
      <Button tone="quiet" testId="skip" busy={busy} onClick={onSkip}>
        Skip next pouch
      </Button>
      <Button tone="quiet" testId="move" busy={busy} onClick={() => onPanel("move")}>
        Move ship date
      </Button>
    </>;
}
function Ready({
  sub,
  panel,
  busy,
  failed,
  note,
  onPanel,
  onRun,
  onSkip,
  onPortal
}: {
  readonly sub: AccountSubscription;
  readonly panel: Panel;
  readonly busy: boolean;
  readonly failed: boolean;
  readonly note: string | null;
  readonly onPanel: (panel: Panel) => void;
  readonly onRun: (action: Action, body?: Readonly<Record<string, string>>) => void;
  readonly onSkip: () => void;
  readonly onPortal: () => void;
}) {
  const live = sub.status === "active" || sub.status === "paused" || sub.status === "past_due";
  return <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl" data-testid="billing">
      <Lead sub={sub} />
      {failed ? <Status state="problem">We couldn't save that. Check your connection and try again.</Status> : null}
      {busy ? <Status>Saving your subscription</Status> : null}
      {note === null || busy ? null : <Status state="ok">{note}</Status>}
      <RestartPath sub={sub} busy={busy} onRestart={() => onRun("restart")} />
      <div className="x78zum5 x1a02dak x626il7">
        <LiveActions sub={sub} busy={busy} onPanel={onPanel} onSkip={onSkip} />
        {sub.status !== "none" ? <Button tone="quiet" testId="portal" busy={busy} onClick={onPortal}>
            Update card and address
          </Button> : null}
        {live ? <Button tone="quiet" testId="cancel" busy={busy} onClick={() => onPanel("cancel")}>
            Cancel
          </Button> : null}
      </div>
      {panel === "pause" ? <PausePanel busy={busy} onPause={months => onRun("pause", {
      months
    })} onBack={() => onPanel(null)} /> : null}
      {panel === "move" ? <MovePanel busy={busy} onMove={days => onRun("move", {
      days
    })} onBack={() => onPanel(null)} /> : null}
      {panel === "cancel" ? <CancelPanel busy={busy} canSkip={sub.status === "active"} onPause={() => onPanel("pause")} onSkip={onSkip} onConfirm={() => onRun("cancel")} onBack={() => onPanel(null)} /> : null}
    </div>;
}

/** Sends the confirmation link again to the address the account is signed in with. */
function ResendConfirmation() {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const send = async () => {
    setState("sending");
    const email = await signedInEmail();
    if (email === null) {
      setState("failed");
      return;
    }
    const outcome = await turnstileToken().then(token => captureEmail(email, token)).catch(() => "failed" as const);
    setState(outcome === "sent" ? "sent" : "failed");
  };
  return <div className="x78zum5 xdt5ytf x15agyyk x1cy8zhl">
      <Button tone="quiet" testId="resend-confirmation" busy={state === "sending"} onClick={() => void send()}>
        Send the link again
      </Button>
      {state === "sent" ? <p role="status" className="x1ghz6dp x1jty25b x1fzhlzt">
          Sent again. Check your spam folder.
        </p> : null}
      {state === "failed" ? <p role="alert" className="x1ghz6dp x1jty25b x1fzhlzt">
          We couldn't send it. Check your connection and try again.
        </p> : null}
    </div>;
}
export function AccountBilling() {
  const [screen, setScreen] = useState<Screen>({
    kind: "loading"
  });
  const [panel, setPanel] = useState<Panel>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [skipping, setSkipping] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const load = () => {
    setScreen({
      kind: "loading"
    });
    void loadAccountSubscription().then(loaded => {
      if (loaded === "signed_out") setScreen({
        kind: "signed_out"
      });else if (loaded === "unconfirmed") setScreen({
        kind: "unconfirmed"
      });else if (loaded === null) setScreen({
        kind: "failed"
      });else setScreen({
        kind: "ready",
        sub: loaded
      });
    }, () => setScreen({
      kind: "failed"
    }));
  };
  useEffect(() => {
    document.title = "Your subscription";
    load();
  }, []);
  const run = async (action: Action, body: Readonly<Record<string, string>> = {}): Promise<AccountSubscription | null> => {
    if (busy) return null;
    setBusy(true);
    setNote(null);
    setFailed(false);
    const next = await changeAccountSubscription(action, body).catch(() => null);
    setBusy(false);
    if (next === null) {
      setFailed(true);
      return null;
    }
    setPanel(null);
    setScreen({
      kind: "ready",
      sub: next
    });
    return next;
  };

  /* The dates the Worker will use, so the question and the note say the real ones. */
  const today = new Date().toISOString();
  const shipOn = screen.kind === "ready" ? screen.sub.nextShipOn : null;
  const skipFrom = currentShipOn(shipOn, today);
  const skipTo = skipShipOn(shipOn, today);
  const skip = async (): Promise<string | null> => {
    const skipped = await run("skip");
    if (skipped !== null) {
      /* The date the Worker applied, which is not the one the question showed if another tab moved it. */
      setNote(`Skipped. Your next pouch now ships on ${day(skipped.nextShipOn ?? skipTo)}.`);
      return null;
    }

    /* The dialog says it, so the page behind does not say it twice. */
    setFailed(false);
    return "We couldn't skip your next pouch. Try again.";
  };
  const portal = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    const session = await openAccountPortal().catch(() => null);
    setBusy(false);
    if (session === null) {
      setFailed(true);
      return;
    }
    window.location.assign(session.url);
  };
  if (screen.kind === "loading") return <AccountShell title="Your subscription">
        <Pending message="Loading your subscription" bones={["title", "line", "control"]} />
      </AccountShell>;
  if (screen.kind === "signed_out") return <AccountShell title="Your subscription" signedOut gateTitle="Sign in to manage billing">
        {null}
      </AccountShell>;
  if (screen.kind === "unconfirmed") return <AccountShell title="Your subscription">
        <p role="alert" className="x1ghz6dp x1jty25b x1fzhlzt">
          Confirm your email first. Open the link we sent you, then come back to manage billing.
        </p>
        <ResendConfirmation />
        <LinkButton href="/support" tone="quiet">
          Contact support
        </LinkButton>
      </AccountShell>;
  if (screen.kind === "failed") return <AccountShell title="Your subscription">
        <ErrorState variant="page" focusOnShow title="We couldn't open your subscription" body={TRY_AGAIN_THEN_SUPPORT} onRetry={load} />
      </AccountShell>;
  return <AccountShell title="Your subscription">
      <Ready sub={screen.sub} panel={panel} busy={busy} failed={failed} note={note} onPanel={setPanel} onRun={(action, body) => void run(action, body)} onSkip={() => setSkipping(true)} onPortal={() => void portal()} />
      <ConfirmDialog open={skipping} onClose={() => setSkipping(false)} title="Skip your next pouch?" confirmLabel="Skip pouch" cancelLabel="Keep pouch" testId="skip-dialog" fallbackFocus={pageHeading} onConfirm={skip}>
        Your next pouch ships on {day(skipFrom)}. Skipping it moves that to {day(skipTo)}, and a
        skip can't be undone from your account. This pouch is not mixed or shipped. Your
        subscription and formula stay as they are.
      </ConfirmDialog>
    </AccountShell>;
}